/**
 * The success screen.
 *
 * Two things are on it and neither is invented: the seat count and the receipt
 * number both come from the `/pay` response, which reads them off the frozen
 * donation snapshot. The share message is assembled from locale strings for the
 * same reason — a hardcoded "Rec-123456" is a number no donor's accountant can
 * reconcile.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface SuccessScreenProps {
  seats: number;
  receiptNumber?: string;
}

export default function SuccessScreen({ seats, receiptNumber }: SuccessScreenProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window === 'undefined' ? '' : window.location.href;
  const shareText = `${t('checkout.successTitle')} — ${t('checkout.receiptNumber')}: ${
    receiptNumber ?? ''
  }`;

  return (
    <section data-testid="stage-success">
      <h1>{t('checkout.successTitle')}</h1>
      <p data-testid="seats-funded">
        {t('checkout.seatsFunded')}: {seats}
      </p>
      {receiptNumber ? (
        <p data-testid="receipt-number">
          {t('checkout.receiptNumber')}: {receiptNumber}
        </p>
      ) : null}
      <button
        type="button"
        data-testid="share-whatsapp"
        onClick={() => {
          window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`);
        }}
      >
        {t('checkout.shareWhatsApp')}
      </button>
      <button
        type="button"
        data-testid="copy-link"
        onClick={() => {
          void navigator.clipboard?.writeText(shareUrl).then(() => setCopied(true));
        }}
      >
        {t('checkout.copyLink')}
      </button>
      {copied ? <p data-testid="copied">{t('checkout.copied')}</p> : null}
    </section>
  );
}
